import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { isOrderAttrFilterActive } from 'src/components/dispatch/orderAttrFilter';
import { orderAttrFilterToken } from 'src/components/dispatch/orderAttrFilterParams';
import { useDebouncedOrderAttrFilter } from 'src/components/dispatch/useDebouncedOrderAttrFilter';
import { useOrderAttrFilter } from 'src/components/dispatch/useOrderAttrFilter';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  useConvenienceDispatchAllDeliveriesQuery,
  useConvenienceDispatchDeliveriesQuery,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useApplyTimetableMutation } from 'src/hooks/useConvenienceDispatchMutation';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { findLegRunIdsToSupplement, useQuerySupplementalLegRuns } from 'src/hooks/useQuerySupplementalLegRuns';
import { useSelectedTimetableId } from 'src/hooks/useSelectedTimetableId';
import { TIMETABLES_QUERY_KEY, useTimetablesQuery } from 'src/hooks/useTimetables.query';
import { useTimetableTripGroupsQuery } from 'src/hooks/useTimetableTripGroups.query';
import { DateModel } from 'src/models/DateModel';
import { normalizePlanningEndOn } from 'src/utils/planningDateRange.util';

import { buildApplyNotifications, describeApplyError } from './needsReview';
import ConvenienceDispatchesPresenter from './Presenter';

// 全ページを並列で取り切るので、この値はレスポンスの分割幅ではなく「同時に飛ぶ本数」を決める。
// 62 台規模で 4 ページ。小さくするとブラウザの同一オリジン 6 並列に当たってキューが伸び、
// 大きくすると 1 本のサーバ処理が重くなる（log_web_issues#301 の計測）
const PER_PAGE_DELIVERIES = 20;
const PER_PAGE_LEG_RUNS = 200;

const ConvenienceDispatches: FC = memo(() => {
  const { startOn: startOnParam, endOn: endOnParam } = useParams<{ startOn: string; endOn: string }>();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);

  // path param が無い / 開始日が日付として不正な場合は既定日へ、
  // レンジが不正（終了日 < 開始日・上限日数超過）な場合は終了日を開始日へ揃えてリダイレクトする。
  // V3Plans / V3Timeline と判定を揃えて、不正な URL を直接開いても壊れないようにする。
  useEffect(() => {
    if (!startOnParam || !endOnParam || new DateModel(startOnParam).isInValid()) {
      const on =
        licenseContext.config?.on ??
        localStorage.getItem('planningOn') ??
        datetimeDecorator.toYyyyMmDd(new Date());
      void navigate(`/convenience-dispatches/${on}/${on}`, { replace: true });
      return;
    }

    const { endOn: normalizedEndOn, changed } = normalizePlanningEndOn(startOnParam, endOnParam);
    if (changed) {
      void navigate(`/convenience-dispatches/${startOnParam}/${normalizedEndOn}`, { replace: true });
    }
  }, [startOnParam, endOnParam, licenseContext.config?.on, navigate]);

  const startOn = startOnParam ?? '';
  const endOn = endOnParam ?? startOn;

  // その日のダイヤ。日付ごとに DB に持ち、会社内で共有する（log_web_issues#361）。
  // 呼び出し・対応づけ・ダイヤとして保存では API が同じ transaction で書くので、ここでは PUT しない（設計書 決定 4・log_web_issues#403 の 2）
  const [timetableId] = useSelectedTimetableId(startOn);
  const { data: timetables = [], isLoading: isLoadingTimetables } = useTimetablesQuery();
  // 便グループ（要確認・本日なし・ダイヤ外・placeholder 行の材料）。複数日でも開始日のもの
  const tripGroupsQuery = useTimetableTripGroupsQuery(timetableId, startOn);

  // V3Plans / V3Timeline と同様、現在の期間を plansContext / licenseContext に反映する
  useEffect(() => {
    if (!startOn || !endOn) return;
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOn, endOn]);

  // 表示対象（配送形態・区間種別）。取得条件なので Container で持つ（/map と同じ）
  const [orderAttrFilter, setOrderAttrFilter] = useOrderAttrFilter();
  // 取得（3 クエリ）だけデバウンスする。チェックの見た目とクライアント側の絞り込みは
  // orderAttrFilter をそのまま使うので、体感の即時性は変えない (issue #317 レビュー I-2)
  const debouncedOrderAttrFilter = useDebouncedOrderAttrFilter(orderAttrFilter);
  // 取得条件（debounce 後）と画面の状態（即時）が食い違っている間も運賃計算を止める。
  // Why not 即時値だけを見る: 解除した瞬間はガードが外れるのに、クエリはまだ絞り込み済みの
  // 結果を持っている（キーが変わるのは 400ms 後）。その窓で押すと一部の車両だけ再計算される
  // (/map と同じ理由。Codex レビュー P1-a)
  const isTariffBlockedByOrderAttrFilter =
    isOrderAttrFilterActive(orderAttrFilter)
    || orderAttrFilterToken(orderAttrFilter) !== orderAttrFilterToken(debouncedOrderAttrFilter);

  const deliveriesQuery = useConvenienceDispatchDeliveriesQuery(startOn, endOn, PER_PAGE_DELIVERIES, debouncedOrderAttrFilter);

  const legRunsQuery = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, PER_PAGE_LEG_RUNS, debouncedOrderAttrFilter);

  // 未割当一覧は積地の時刻で引くので、前日に積んで当日は降ろすだけの案件（宵積み）が
  // 入らない。deliveries の compact な legRun を補えるよう、一覧に無い案件を別に引く
  // (log_web_issues#343)。一覧が届く前に比べると全件が「無い」扱いになるので待つ
  const supplementalLegRunIds = useMemo(
    () => (legRunsQuery.isSuccess
      ? findLegRunIdsToSupplement(
        deliveriesQuery.data?.pages.flatMap((p) => p.data) ?? [],
        legRunsQuery.data.pages.flatMap((p) => p.data),
      )
      : []),
    [deliveriesQuery.data, legRunsQuery.data, legRunsQuery.isSuccess],
  );
  const supplementalLegRuns = useQuerySupplementalLegRuns(supplementalLegRunIds);

  // 運賃計算の対象決定用: ダイヤ選択時のみ対象日全件を取る（左カラム表示の pagination とは分離）
  const allDeliveriesQuery = useConvenienceDispatchAllDeliveriesQuery(startOn, endOn, timetableId != null, debouncedOrderAttrFilter);

  // VehicleInfo 表示用のトラック/ドライバーのマスタ（V3Plans/Timeline と同じ期間指定 hook）
  const trucksQuery = useQueryPlanningsTrucks(startOn, endOn);
  const driversQuery = useQueryPlanningsDrivers(startOn, endOn);

  const applyTimetable = useApplyTimetableMutation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // Backdrop メッセージ切り替え用: 'call' = 呼び出し, 'align' = 対応づけ（Presenter 側で applyChainBusy 判定により表示制御）
  const [applyMode, setApplyMode] = useState<'call' | 'align'>('align');

  const handleDateRangeChange = useCallback(
    (start: string, end: string) => {
      if (start === '') return;
      // DateRangePicker は range で start / end の双方を渡す。end が未指定 (= 単日選択) なら start にフォールバックする。
      const safeEnd = end || start;
      void navigate(`/convenience-dispatches/${start}/${safeEnd}`, { replace: false });
    },
    [navigate]
  );

  // 呼び出し（createOperations: false）と対応づけ（true）。成功後の同期（daily・便グループ・deliveries・leg runs）は
  // mutation hook の onSuccess が await するので、ここは通知だけ
  const runApply = useCallback((targetId: number, mode: 'call' | 'align') => {
    if (startOn === '') return;
    setApplyMode(mode);
    applyTimetable.mutate(
      { timetableId: targetId, planningDate: startOn, createOperations: mode === 'align' },
      {
        onSuccess: (result) => {
          buildApplyNotifications(result, mode).forEach(({ message, variant }) => enqueueSnackbar(message, { variant }));
        },
        onError: (error) => {
          const { message, notFound } = describeApplyError(error, mode);
          enqueueSnackbar(message, { variant: 'error' });
          // 消されたダイヤが一覧に残らないように
          if (notFound) void queryClient.invalidateQueries({ queryKey: [TIMETABLES_QUERY_KEY] });
        },
      },
    );
  }, [startOn, applyTimetable, enqueueSnackbar, queryClient]);
  const handleApplyTimetable = useCallback((id: number) => runApply(id, 'call'), [runApply]);
  const handleAlignToTimetable = useCallback((id: number) => runApply(id, 'align'), [runApply]);

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate]
  );

  // 未割当リストの32列フィルタ向けの選択肢・準備状態。Map/index.tsx と同じ導出。
  const trucksForFilter = useMemo(
    () => (trucksQuery.data ?? []).map((t) => ({ id: t.id, name: t.truckNumber || t.licensePlateValue })),
    [trucksQuery.data],
  );
  const driversForFilter = useMemo(
    () => (driversQuery.data ?? []).map((d) => ({ id: d.id, name: d.name })),
    [driversQuery.data],
  );
  const customInputFieldNames = useMemo(
    () => licenseContext.config?.custom_input_fields ?? [],
    [licenseContext.config],
  );

  return (
    <ConvenienceDispatchesPresenter
      startOn={startOn}
      endOn={endOn}
      timetableId={timetableId}
      timetables={timetables}
      isLoadingTimetables={isLoadingTimetables}
      isApplying={applyTimetable.isPending}
      deliveriesQuery={deliveriesQuery}
      legRunsQuery={legRunsQuery}
      supplementalLegRuns={supplementalLegRuns}
      tripGroupsQuery={tripGroupsQuery}
      allDeliveriesQuery={allDeliveriesQuery}
      orderAttrFilter={orderAttrFilter}
      onOrderAttrFilterChange={setOrderAttrFilter}
      isTariffBlockedByOrderAttrFilter={isTariffBlockedByOrderAttrFilter}
      trucks={trucksQuery.data ?? []}
      drivers={driversQuery.data ?? []}
      onDateRangeChange={handleDateRangeChange}
      onApplyTimetable={handleApplyTimetable}
      onAlignToTimetable={handleAlignToTimetable}
      applyMode={applyMode}
      onNavigate={handleNavigate}
      customInputFieldNames={customInputFieldNames}
      trucksForFilter={trucksForFilter}
      driversForFilter={driversForFilter}
      companyId={String(licenseContext.config?.selected_company_id ?? 0)}
      isLicenseReady={Boolean(licenseContext.config)}
      useAlgorithmPlanning={Boolean(licenseContext.config?.use_algorithm_planning)}
      deliveryListGrouping={licenseContext.config?.delivery_list_grouping ?? 'groupByAddress'}
      selectedCompanyId={licenseContext.config ? licenseContext.config.selected_company_id : null}
      isMastersReady={trucksQuery.isSuccess && driversQuery.isSuccess}
      isMastersError={trucksQuery.isError || driversQuery.isError}
    />
  );
});

export default ConvenienceDispatches;
